import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Star, Edit2, Trash2, Send } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

/**
 * <ReviewsBlock subjectKind="adventure"|"marketplace"|"gear" subjectId="..." />
 * Lists reviews for the given subject + lets the current user write/edit/delete their own.
 */
export default function ReviewsBlock({ subjectKind, subjectId, title = "Reviews" }) {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [avg, setAvg] = useState(0);
    const [loading, setLoading] = useState(true);
    const [composing, setComposing] = useState(false);
    const [draft, setDraft] = useState({ rating: 0, title: "", text: "" });

    const myReview = items.find((r) => r.author_id === user?.user_id);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/reviews", { params: { subject_kind: subjectKind, subject_id: subjectId } });
            setItems(data.items);
            setAvg(data.avg_rating);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!subjectId) return;
        load();
    }, [subjectKind, subjectId]); // eslint-disable-line react-hooks/exhaustive-deps

    const startEdit = (rev) => {
        setDraft({ rating: rev.rating, title: rev.title, text: rev.text });
        setComposing(true);
    };

    const submit = async (e) => {
        e?.preventDefault();
        if (!draft.rating) { toast.error("Pick a rating"); return; }
        try {
            await api.post("/reviews", {
                subject_kind: subjectKind,
                subject_id: subjectId,
                rating: draft.rating,
                title: draft.title.trim(),
                text: draft.text.trim(),
            });
            toast.success(myReview ? "Review updated" : "Review posted");
            setComposing(false);
            setDraft({ rating: 0, title: "", text: "" });
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const remove = async (id) => {
        if (!confirm("Delete this review?")) return; // eslint-disable-line no-alert
        try {
            await api.delete(`/reviews/${id}`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div data-testid={`reviews-block-${subjectKind}`} className="bg-snowcap border border-ridge-line rounded-lg p-5">
            <header className="flex items-center justify-between mb-4">
                <div>
                    <h3 className="font-display font-black text-lg text-midnight-pine">{title}</h3>
                    {items.length > 0 && (
                        <div className="flex items-center gap-2 mt-1">
                            <Stars value={avg} small />
                            <span className="text-xs font-bold text-granite">{avg.toFixed(1)}</span>
                            <span className="text-xs text-slate-gray">({items.length})</span>
                        </div>
                    )}
                </div>
                {user && !composing && (
                    <button
                        data-testid="review-write-btn"
                        onClick={() => { setDraft(myReview ? { rating: myReview.rating, title: myReview.title, text: myReview.text } : { rating: 0, title: "", text: "" }); setComposing(true); }}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-ember-orange text-white text-xs font-bold hover:bg-ember-dark transition-all"
                    >
                        <Edit2 className="w-3.5 h-3.5" /> {myReview ? "Edit your review" : "Write a review"}
                    </button>
                )}
            </header>

            {composing && (
                <form onSubmit={submit} data-testid="review-compose-form" className="space-y-3 mb-4 p-4 rounded-md bg-alpine-mist border border-ridge-line">
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-widest text-slate-gray block mb-1">Rating</label>
                        <Stars value={draft.rating} onChange={(v) => setDraft((d) => ({ ...d, rating: v }))} testid="review-rating" />
                    </div>
                    <input
                        data-testid="review-title-input"
                        value={draft.title}
                        onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                        maxLength={120}
                        placeholder="Headline (optional)"
                        className="w-full px-3 py-2 rounded-md bg-snowcap border border-ridge-line text-sm focus:outline-none focus:border-ember-orange"
                    />
                    <textarea
                        data-testid="review-text-input"
                        value={draft.text}
                        onChange={(e) => setDraft((d) => ({ ...d, text: e.target.value }))}
                        maxLength={4000}
                        rows={3}
                        placeholder="What did you think?"
                        className="w-full px-3 py-2 rounded-md bg-snowcap border border-ridge-line text-sm focus:outline-none focus:border-ember-orange resize-none"
                    />
                    <div className="flex items-center gap-2 justify-end">
                        <button type="button" onClick={() => setComposing(false)} className="px-3 py-1.5 rounded-md text-xs font-bold border border-ridge-line text-granite hover:bg-snowcap">Cancel</button>
                        <button type="submit" data-testid="review-submit-btn" className="inline-flex items-center gap-1 px-4 py-1.5 rounded-md bg-ember-orange text-white text-xs font-bold hover:bg-ember-dark"><Send className="w-3.5 h-3.5" /> {myReview ? "Update" : "Post"}</button>
                    </div>
                </form>
            )}

            {loading ? (
                <div className="space-y-2">{[0, 1].map((i) => <div key={i} className="h-16 bg-alpine-mist rounded-md animate-pulse" />)}</div>
            ) : items.length === 0 ? (
                <p className="text-sm text-slate-gray italic text-center py-6">No reviews yet — be the first.</p>
            ) : (
                <ul className="space-y-3" data-testid="reviews-list">
                    {items.map((r) => (
                        <li key={r.id} data-testid={`review-${r.id}`} className="border-t border-ridge-line pt-3 first:border-0 first:pt-0">
                            <div className="flex items-start gap-3">
                                <div className="w-9 h-9 rounded-full overflow-hidden bg-trail-tan flex items-center justify-center text-midnight-pine font-bold shrink-0">
                                    {r.author?.avatar ? <img src={r.author.avatar} alt="" className="w-full h-full object-cover" /> : (r.author?.name?.[0] || "?")}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-bold text-sm text-midnight-pine">{r.author?.name || "Unknown"}</span>
                                        <Stars value={r.rating} small />
                                        <span className="text-[10px] text-slate-gray">{formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}</span>
                                        {r.author_id === user?.user_id && (
                                            <button onClick={() => startEdit(r)} className="text-[10px] text-ember-orange font-bold ml-auto hover:underline">Edit</button>
                                        )}
                                        {(r.author_id === user?.user_id || user?.role === "admin") && (
                                            <button data-testid={`review-delete-${r.id}`} onClick={() => remove(r.id)} className="text-slate-gray hover:text-trail-red"><Trash2 className="w-3.5 h-3.5" /></button>
                                        )}
                                    </div>
                                    {r.title && <div className="font-bold text-sm text-granite mt-0.5">{r.title}</div>}
                                    {r.text && <p className="text-sm text-granite leading-relaxed mt-1 whitespace-pre-line">{r.text}</p>}
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function Stars({ value, onChange, small, testid }) {
    const size = small ? "w-3.5 h-3.5" : "w-5 h-5";
    return (
        <div className="inline-flex items-center gap-0.5" data-testid={testid}>
            {[1, 2, 3, 4, 5].map((n) => (
                <button
                    key={n}
                    type="button"
                    data-testid={testid ? `${testid}-${n}` : undefined}
                    onClick={onChange ? () => onChange(value === n ? 0 : n) : undefined}
                    className={onChange ? "p-0.5" : "pointer-events-none"}
                    aria-label={`${n} stars`}
                >
                    <Star className={`${size} ${n <= value ? "fill-ember-orange text-ember-orange" : "text-ridge-line"}`} />
                </button>
            ))}
        </div>
    );
}
